import { intersection } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { LOCAL_STORAGE_KEYS, routes, type SsoUserGroup } from '#shared';

import { useQueryGrantedGroups } from '../../api';

import type { QueryStatus } from '@tanstack/react-query';

const statsRootRoute = routes.STATS.replace('*', '');
const defaultPages = [statsRootRoute, routes.START_PAGE];
const key = LOCAL_STORAGE_KEYS.GRANTED_GROUPS;
const initialGrantedGroups = localStorage.getItem(key);

export const useSsoUserGroupAccess = (
  groups?: SsoUserGroup[],
): {
  currentAccess?: boolean;
  grantedGroups: SsoUserGroup[];
  defaultPage?: string;
  isDefaultPage: boolean;
  status: QueryStatus;
} => {
  const { pathname } = useLocation();

  const { status, data } = useQueryGrantedGroups();

  const grantedGroups = useMemo(() => {
    if (data && data.length > 0) {
      return data;
    }
    return (initialGrantedGroups && JSON.parse(initialGrantedGroups)) || [];
  }, [data]);

  useEffect(() => {
    if (data && data.length > 0) {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.GRANTED_GROUPS,
        JSON.stringify(data),
      );
    }
  }, [data]);

  const isDefaultPage = defaultPages.includes(pathname);

  const defaultPage = useMemo(() => {
    if (!grantedGroups) {
      return;
    }
    const hasCCAccess =
      intersection(grantedGroups, ['CommandCenter', 'Admin']).length > 0;

    // Always prefer CommandCenter (Admin also has CC)
    if (hasCCAccess) {
      return routes.MAIN_MAP;
    }
    // If has only Stats — go to Stats
    if (grantedGroups.includes('Statistics')) {
      return statsRootRoute;
    }
    return;
  }, [grantedGroups]);

  const currentAccess = useMemo(
    () => groups && !!intersection(grantedGroups, ['Admin', ...groups]).length,
    [grantedGroups, groups],
  );

  return {
    defaultPage,
    grantedGroups,
    isDefaultPage,
    status,
    currentAccess,
  };
};
